import { useCallback, useEffect, useState } from 'react'
import { useRouter } from 'next/router'

import { isAuthenticated, selectUserId } from '~/auth/slices/auth.slice'
import {
  useAcknowledgeProgressMigrationModalMutation,
  useGetProgressMigrationModalStatusQuery,
} from '~/curriculum/apis/curriculum-progress.api'
import { ProgressMigrationFlowPhase } from '~/curriculum/types/progress-migration.types'
import {
  isProgressMigrationModalHandled,
  markProgressMigrationModalHandled,
} from '~/curriculum/utils/progressMigrationModalSession.utils'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { selectCurrentUser } from '~/redux/slices/user'
import { setProgressMigrationFlowOpen } from '~/redux/slices/modals'

const PROGRESS_MIGRATION_CUTOFF = new Date('2026-04-13T18:50:00Z')

const MIGRATION_MODAL_ALLOWED_PATHS = ['curriculum', 'bootcamp', 'dashboard', 'my-statistics', 'math-vault']

const POST_ACK_LOADER_MS = 3000
const MIGRATION_STATUS_POLL_INTERVAL_MS = 30000

interface UseProgressMigrationModalResult {
  acknowledgeModal: () => Promise<void>
  completeFlow: () => void
  flowPhase: ProgressMigrationFlowPhase
  isAcknowledging: boolean
  shouldShowFlow: boolean
}

export default function useProgressMigrationModal(): UseProgressMigrationModalResult {
  const router = useRouter()
  const dispatch = useAppDispatch()
  const authenticated = useAppSelector(isAuthenticated)
  const userId = useAppSelector(selectUserId)
  const currentUser = useAppSelector(selectCurrentUser)
  const isBeforeCutoff = currentUser?.createdAt ? new Date(currentUser.createdAt) < PROGRESS_MIGRATION_CUTOFF : false
  const [sessionHandled, setSessionHandled] = useState(() => (userId ? isProgressMigrationModalHandled(userId) : false))
  const [flowPhase, setFlowPhase] = useState<ProgressMigrationFlowPhase>('idle')
  const [minTimeElapsed, setMinTimeElapsed] = useState(false)
  const isAllowedPath = MIGRATION_MODAL_ALLOWED_PATHS.some((path) => router.asPath.includes(path))

  useEffect(() => {
    setSessionHandled(userId ? isProgressMigrationModalHandled(userId) : false)
    setFlowPhase('idle')
  }, [userId])

  // Skip the query only when sessionHandled AND not in postAckLoader (we still need to poll during animation)
  const skipQuery =
    !authenticated || !isBeforeCutoff || !userId || (sessionHandled && flowPhase !== 'postAckLoader') || !isAllowedPath

  const { data, refetch } = useGetProgressMigrationModalStatusQuery(undefined, {
    skip: skipQuery,
    pollingInterval: flowPhase === 'postAckLoader' ? MIGRATION_STATUS_POLL_INTERVAL_MS : 0,
  })

  useEffect(() => {
    if (!skipQuery) {
      refetch()
    }
  }, [router.asPath, skipQuery, refetch])

  // Mark session handled immediately when API confirms both acknowledged and migration finished
  useEffect(() => {
    if (!userId) return
    if (!data?.modalAcknowledged) return
    if (!data?.migrationStatus || data.migrationStatus === 'pending') return

    markProgressMigrationModalHandled(userId)
    setSessionHandled(true)
  }, [data?.modalAcknowledged, data?.migrationStatus, userId])

  useEffect(() => {
    if (sessionHandled) return
    if (data?.migrationStatus === null || data?.migrationStatus === undefined) return
    if (flowPhase !== 'idle') return

    if (!data.modalAcknowledged && data.migrationStatus !== 'failed') {
      setFlowPhase('modal')
    } else if (data.migrationStatus === 'pending') {
      // Acknowledged in a previous session but migration still running — resume loader
      setFlowPhase('postAckLoader')
    }
  }, [data?.migrationStatus, data?.modalAcknowledged, flowPhase, sessionHandled])

  const [acknowledge, { isLoading: isAcknowledging }] = useAcknowledgeProgressMigrationModalMutation()

  const acknowledgeModal = useCallback(async (): Promise<void> => {
    try {
      await acknowledge().unwrap()
      setMinTimeElapsed(false)
      setFlowPhase('postAckLoader')
    } catch {
      // Keep modal open so the user can retry.
    }
  }, [acknowledge])

  const completeFlow = useCallback((): void => {
    setFlowPhase('idle')
  }, [])

  // Start minimum-time timer when postAckLoader phase begins
  useEffect(() => {
    if (flowPhase !== 'postAckLoader') {
      setMinTimeElapsed(false)
      return undefined
    }

    const timeoutId = window.setTimeout(() => {
      setMinTimeElapsed(true)
    }, POST_ACK_LOADER_MS)

    return () => window.clearTimeout(timeoutId)
  }, [flowPhase])

  // Transition to done when min time has elapsed AND migration is no longer pending
  // AND the user is in curriculum v2 — the PostHog flag flips only once the user is
  // fully migrated, so until then we keep showing the loader instead of completing.
  useEffect(() => {
    if (flowPhase !== 'postAckLoader') return
    if (!minTimeElapsed) return
    if (data?.migrationStatus === 'pending') return

    setFlowPhase('done')
  }, [flowPhase, minTimeElapsed, data?.migrationStatus])

  // Only mark the full flow as handled once migration is complete
  useEffect(() => {
    if (flowPhase === 'done' && userId) {
      markProgressMigrationModalHandled(userId)
      setSessionHandled(true)
    }
  }, [flowPhase, userId])

  const shouldShowFlow = isAllowedPath && (flowPhase === 'modal' || flowPhase === 'postAckLoader')

  useEffect(() => {
    dispatch(setProgressMigrationFlowOpen(shouldShowFlow))

    return () => {
      dispatch(setProgressMigrationFlowOpen(false))
    }
  }, [dispatch, shouldShowFlow])

  return {
    acknowledgeModal,
    completeFlow,
    flowPhase,
    isAcknowledging,
    shouldShowFlow,
  }
}

export { POST_ACK_LOADER_MS }
